/**
 * search-bar.js — Host-page search bar (optional, config-driven).
 *
 * STAGE: Optional — runs only for a winery whose tenant config supplies a
 *        `search_bar` object AND leaves `ui_features.show_search_bar` on.
 *        No config = no search bar, so no existing tenant is affected.
 *
 * WHAT IT IS:
 *   A second entry point into the same conversation, spliced into the winery's
 *   OWN page (typically their nav): an input + a grouped suggestions dropdown.
 *   Submitting does not jump straight to the panel — the dropdown turns into a
 *   loading state, and the chat panel opens once the answer has landed, so the
 *   visitor keeps looking at the winery's page while the bot thinks.
 *
 * PORTED FROM:
 *   Old fork main.js 2790-3191 ("Searchbar section"). That version hard-coded
 *   Goosecross's Webflow selectors (`.nav`, `.w-layout-hflex.top-nav`,
 *   `#menu-button`), its six suggestions, and its placeholder. Everything that
 *   was hard-coded there is tenant config here — see CONFIG_CONTRACT.md
 *   `search_bar`, and `variants/goosecross/index.js` getSearchBar() for the
 *   fallback used until the values are in the admin config.
 *
 * ISOLATION:
 *   The bar lives in the winery's DOM but gets its OWN shadow root, so their
 *   CSS cannot reach it and ours cannot leak out. It carries the same
 *   `ws-tenant-<slug>` classes as the panel root, so a variant themes it from
 *   its normal `variants/<slug>/styles.css`.
 *
 * EVENTS CONSUMED: ws-chat-settled, ws-toggle
 * EVENTS EMITTED:  ws-chat-request { text, source: 'search' }, ws-toggle,
 *                  ws-scroll-bottom
 */

import {
  on,
  emit,
  addMessage,
  getState,
  updateChatState,
  isChatDisabled,
} from "../../core/state.js";
import { getSearchBar, getUiFeature, getTenantId } from "../../core/configAccessors.js";
import { applyTheme } from "../../core/theme.js";
import variablesCSS from "../../styles/variables.css";
import searchBarCSS from "../../styles/SearchBar.css";
import { TENANT_CSS } from "../../styles/tenant-css.js";
// The old fork's "generating response" animation (grape dots), served there from
// static/images/search-loading.gif. Inlined as a data: URI by the build's .gif
// loader. A winery can point `search_bar.loading_image_url` at its own instead.
import searchLoadingGif from "../../assets/images/search-loading.gif";

const CONTAINER_ID = "redconcierge-search";
// Distinguishes this shadow host from the widget panel's — see mount().
const SEARCH_HOST_CLASS = "ws-search-host";

// How long to keep looking for the winery's mount node. Their nav is often
// built by their own JS (Webflow), so it may not exist when we boot — the old
// widget just guessed with `setTimeout(createSearchBar, 2000)`. We observe the
// DOM instead and give up quietly after this long.
const MOUNT_TIMEOUT_MS = 15000;

const ICON_MAGNIFIER = `<svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14.07 12.2A7.33 7.33 0 1 0 12.87 13.4L17.31 17.83L18.5 16.64L14.07 12.2ZM8.33 13.48A5.83 5.83 0 1 1 8.33 1.82A5.83 5.83 0 0 1 8.33 13.48Z" fill="currentColor"/>
</svg>`;

const ICON_SPARKLE = `<svg width="13" height="14" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14.4093 11.0789C14.4093 11.1731 14.3462 11.2559 14.2516 11.2832L9.30228 12.7699L7.75112 17.5157C7.72259 17.6056 7.63549 17.6668 7.53788 17.6668C7.43953 17.6668 7.35318 17.6063 7.32465 17.5157L5.77349 12.7699L0.824173 11.2832C0.730322 11.2559 0.666504 11.1724 0.666504 11.0789C0.666504 10.9846 0.729571 10.9018 0.824173 10.8745L5.77349 9.38781L7.32465 4.64426C7.38396 4.4658 7.69405 4.4658 7.75337 4.64426L9.30453 9.38781L14.2538 10.8745C14.3477 10.9018 14.4115 10.9853 14.4115 11.0789H14.4093ZM16.8449 2.64381L14.9822 2.08541L14.3995 0.300104C14.3402 0.121645 14.0301 0.121645 13.9708 0.300104L13.3882 2.08541L11.5255 2.64381C11.4316 2.67115 11.3678 2.75462 11.3678 2.84817C11.3678 2.94244 11.4309 3.02519 11.5255 3.05254L13.3882 3.61094L13.9708 5.39625C13.9994 5.48619 14.0864 5.54736 14.1841 5.54736C14.2824 5.54736 14.3688 5.48691 14.3973 5.39625L14.9799 3.61094L16.8427 3.05254C16.9365 3.0252 17.0003 2.94173 17.0003 2.84817C17.0003 2.75391 16.9373 2.67115 16.8427 2.64381H16.8449Z" fill="url(#ws-search-sparkle)"/>
  <defs><linearGradient id="ws-search-sparkle" x1="8.83" y1="0.17" x2="8.83" y2="17.67" gradientUnits="userSpaceOnUse">
    <stop stop-color="currentColor"/><stop offset="1" stop-color="white"/>
  </linearGradient></defs>
</svg>`;

const ICON_SEND = `<svg width="29" height="24" viewBox="0 0 29 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M0.5 24V15L12.5 12L0.5 9V0L29 12L0.5 24Z" fill="currentColor"/>
</svg>`;

const ICON_CLEAR = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const ICON_ITEM = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <path d="M14.0001 14L11.1335 11.1333M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z" stroke="currentColor" stroke-width="1.33" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

// Group in first-seen order so the admin controls the order of the dropdown by
// the order of the list. Un-categorised items collect under a single unlabelled
// group, which renders without a heading.
function groupByCategory(suggestions) {
  const groups = [];
  const index = new Map();
  for (const item of suggestions) {
    const key = item.category || "";
    if (!index.has(key)) {
      index.set(key, { category: key, items: [] });
      groups.push(index.get(key));
    }
    index.get(key).items.push(item);
  }
  return groups;
}

// Resolve the insertion point from config. Returns null until the winery's own
// markup exists, which is the normal state for the first few hundred ms.
function resolveMount({ selector, position, reference }) {
  const host = document.querySelector(selector);
  if (!host) return null;
  if (position === "before" || position === "after") {
    // `reference` is looked up INSIDE the host so a winery only has to give a
    // short child selector (e.g. "#menu-button"), as the old code did.
    const ref = reference ? host.querySelector(reference) : null;
    if (!ref) return null;
    return { parent: ref.parentNode, before: position === "before" ? ref : ref.nextSibling };
  }
  return { parent: host, before: position === "prepend" ? host.firstChild : null };
}

export class SearchBar {
  constructor(config) {
    this.config = config;
    this.container = null;
    this.shadow = null;
    this.el = {};
    this._pending = false;
    this._clipped = [];
  }

  mount(point) {
    this.container = document.createElement("div");
    this.container.id = CONTAINER_ID;
    // Two classes, and the distinction matters:
    //   ws-tenant-<slug>  same as the panel root, so a variant's `:host(...)`
    //                     var blocks (colours, fonts) reach this bar too.
    //   ws-search-host    marks THIS host as the search bar's. tenant-css.js is
    //                     injected into both roots, so a variant rule meant only
    //                     for the bar — anything positional above all — MUST be
    //                     written `:host(.ws-tenant-<slug>.ws-search-host)`, or
    //                     it also lands on #redconcierge-widget and drags the
    //                     launcher across the page with it.
    this.container.classList.add(SEARCH_HOST_CLASS);
    const tenantId = getTenantId();
    if (tenantId) this.container.classList.add(`ws-tenant-${String(tenantId).toLowerCase().trim()}`);
    point.parent.insertBefore(this.container, point.before);

    this.shadow = this.container.attachShadow({ mode: "open" });
    const style = document.createElement("style");
    style.textContent = [variablesCSS, searchBarCSS, TENANT_CSS].join("\n");
    this.shadow.appendChild(style);
    applyTheme(this.shadow);

    this._render();
    this._wire();
    this._watchViewport();
  }

  _render() {
    const root = document.createElement("div");
    root.className = "ws-search";
    root.innerHTML = `
      <div class="ws-search-bar">
        <span class="ws-search-icons">
          ${ICON_MAGNIFIER}
          <span class="ws-search-sparkle">${ICON_SPARKLE}</span>
        </span>
        <input class="ws-search-input" type="text" autocomplete="off" />
        <button class="ws-search-clear" type="button" hidden aria-label="Clear search">${ICON_CLEAR}</button>
        <button class="ws-search-send" type="button" aria-label="Search">${ICON_SEND}</button>
      </div>
      <div class="ws-search-panel" hidden>
        <div class="ws-search-suggestions" hidden></div>
        <div class="ws-search-loading" hidden>
          <img class="ws-search-loading-img" alt="" />
          <p class="ws-search-loading-text"></p>
        </div>
      </div>
    `;
    this.shadow.appendChild(root);

    this.el = {
      root,
      input: root.querySelector(".ws-search-input"),
      clear: root.querySelector(".ws-search-clear"),
      send: root.querySelector(".ws-search-send"),
      panel: root.querySelector(".ws-search-panel"),
      suggestions: root.querySelector(".ws-search-suggestions"),
      loading: root.querySelector(".ws-search-loading"),
      loadingImg: root.querySelector(".ws-search-loading-img"),
      loadingText: root.querySelector(".ws-search-loading-text"),
    };

    this.el.input.placeholder = this.config.placeholder || "";
    this.el.loadingImg.src = this.config.loading_image_url || searchLoadingGif;
    this.el.loadingText.textContent =
      this.config.loading_text || "Generating response for you...";
    this._renderSuggestions();
  }

  _renderSuggestions() {
    const groups = groupByCategory(this.config.suggestions || []);
    const frag = document.createDocumentFragment();

    groups.forEach((group, idx) => {
      if (group.category) {
        const heading = document.createElement("div");
        heading.className = "ws-search-category";
        heading.textContent = group.category;
        frag.appendChild(heading);
      }
      group.items.forEach((item) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "ws-search-item";
        btn.innerHTML = `<span class="ws-search-item-icon">${ICON_ITEM}</span><span></span>`;
        // textContent, not innerHTML — suggestions are admin-authored config.
        btn.lastElementChild.textContent = item.text;
        btn.addEventListener("click", () => {
          this.el.input.value = item.text;
          this._submit();
        });
        frag.appendChild(btn);
      });
      if (idx < groups.length - 1) {
        const divider = document.createElement("div");
        divider.className = "ws-search-divider";
        frag.appendChild(divider);
      }
    });

    this.el.suggestions.replaceChildren(frag);
  }

  _wire() {
    const { input, clear, send } = this.el;

    input.addEventListener("focus", () => this._open());
    input.addEventListener("click", () => this._open());
    input.addEventListener("input", () => {
      clear.hidden = !input.value;
      this._open();
    });
    input.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this._close();
        input.blur();
      } else if (e.key === "Enter") {
        e.preventDefault();
        this._submit();
      }
    });

    clear.addEventListener("click", () => {
      input.value = "";
      clear.hidden = true;
      input.focus();
    });

    send.addEventListener("click", () => this._submit());

    // Click anywhere else on the winery's page closes the dropdown. The event
    // is retargeted to our host element for clicks inside the shadow root, so
    // a simple contains() check on the container covers both.
    this._onDocClick = (e) => {
      if (!this.container.contains(e.target)) this._close();
    };
    document.addEventListener("click", this._onDocClick);

    // A request that started somewhere else (the panel itself) must not leave
    // this bar stuck in its loading state.
    on("ws-chat-settled", () => this._settle());
    // Opening the panel by hand means the visitor has moved on from the bar.
    on("ws-toggle", ({ isOpen }) => {
      if (isOpen) this._close();
    });
  }

  // The bar only fits a desktop nav; under the winery's mobile breakpoint their
  // nav collapses to a burger and there is nowhere to put it.
  _watchViewport() {
    const min = this.config.min_width;
    if (!min) return;
    const mq = window.matchMedia(`(min-width: ${min}px)`);
    const apply = () => {
      this.container.style.display = mq.matches ? "" : "none";
      if (!mq.matches) this._close();
    };
    apply();
    // Safari <14 only has the deprecated form.
    if (mq.addEventListener) mq.addEventListener("change", apply);
    else mq.addListener(apply);
  }

  /**
   * The dropdown grows the bar downward, so any ancestor on the winery's page
   * that clips its overflow would cut it off. The old widget hard-coded
   * `.nav { overflow: visible }` + hid `.bottom-nav` for Goosecross; this walks
   * up from our own container instead and un-clips whatever it finds, so it
   * works on any winery's markup. Everything is restored on close.
   */
  _unclip() {
    this._restore();
    let node = this.container.parentElement;
    while (node && node !== document.body) {
      const { overflow, overflowX, overflowY } = getComputedStyle(node);
      if ([overflow, overflowX, overflowY].some((v) => v && v !== "visible")) {
        this._clipped.push({ node, prev: node.style.overflow });
        node.style.overflow = "visible";
      }
      node = node.parentElement;
    }
  }

  _restore() {
    this._clipped.forEach(({ node, prev }) => {
      node.style.overflow = prev;
    });
    this._clipped = [];
  }

  _open() {
    if (this._pending) return;
    // Nothing to show is not an open state — leave the bar as a plain input.
    if (!(this.config.suggestions || []).length) return;
    this.el.root.classList.add("is-open");
    this.el.panel.hidden = false;
    this.el.suggestions.hidden = false;
    this._unclip();
    // Old parity: opening the search dropdown closes the chat panel, so the two
    // surfaces are never both competing for the visitor's attention.
    if (getState().chat.isOpen) {
      updateChatState({ isOpen: false });
      emit("ws-toggle", { isOpen: false });
    }
  }

  _close() {
    if (this._pending) return;
    this.el.root.classList.remove("is-open");
    this.el.panel.hidden = true;
    this.el.suggestions.hidden = true;
    this._restore();
  }

  _submit() {
    const text = this.el.input.value.trim();
    if (!text || this._pending || isChatDisabled()) return;

    this._pending = true;
    this.el.root.classList.add("is-open");
    this.el.panel.hidden = false;
    this.el.suggestions.hidden = true;
    this.el.loading.hidden = false;
    this.el.input.disabled = true;
    this.el.send.disabled = true;
    this._unclip();

    addMessage({ role: "user", kind: "text", isFirstResponse: false, data: { text } });
    // source 'search' is what makes chat-handler send is_search=true, which is
    // how the BE splits "messages via search bar" out in the analytics report.
    emit("ws-chat-request", { text, source: "search" });
  }

  // Answer landed (or failed): drop the loading state and hand the visitor over
  // to the panel, which now holds the reply.
  _settle() {
    if (!this._pending) return;
    this._pending = false;
    this.el.loading.hidden = true;
    this.el.input.disabled = false;
    this.el.send.disabled = false;
    this.el.input.value = "";
    this.el.clear.hidden = true;
    this._close();

    if (!getState().chat.isOpen) {
      updateChatState({ isOpen: true });
      emit("ws-toggle", { isOpen: true });
    }
    // The whole turn ran with the panel hidden, so MessageList's own
    // scroll-to-bottom calls did nothing (scrollHeight is 0 on a display:none
    // element) and it would open showing the TOP of the transcript. Ask for the
    // bottom now that the panel is visible — after the ws-toggle above, which
    // sets the `open` attribute synchronously.
    emit("ws-scroll-bottom");
  }
}

// Wait for the winery's mount node, which their own scripts may still be
// building. Resolves null if it never turns up.
function waitForMount(mount) {
  return new Promise((resolve) => {
    const attempt = () => resolveMount(mount);

    const found = attempt();
    if (found) return resolve(found);

    let observer;
    const timer = setTimeout(() => {
      observer?.disconnect();
      resolve(null);
    }, MOUNT_TIMEOUT_MS);

    observer = new MutationObserver(() => {
      const point = attempt();
      if (point) {
        observer.disconnect();
        clearTimeout(timer);
        resolve(point);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  });
}

export async function init() {
  const config = getSearchBar();
  // Two gates. The config is the real one — a winery with no `search_bar`
  // section never gets a bar, which is every tenant today. show_search_bar is
  // then the admin's on/off switch for a winery that HAS been set up.
  if (!config) return;
  if (!getUiFeature("show_search_bar", true)) return;
  if (document.getElementById(CONTAINER_ID)) return;

  if (config.mount.delay_ms > 0) {
    await new Promise((r) => setTimeout(r, config.mount.delay_ms));
  }

  const point = await waitForMount(config.mount);
  if (!point) {
    console.warn(
      `[RedConcierge] search bar: no element matched "${config.mount.selector}" — not mounted`,
    );
    return;
  }

  new SearchBar(config).mount(point);
  console.info("[RedConcierge] Search bar mounted");
}
